import useSWR, { type SWRConfiguration } from 'swr';
import denApi from '@/lib/api/denApi';
import type { AddressBookListEntry } from '@/types/address-book';

type AddressBookEntryByAddressParams = {
  address: string;
};

const fetcher = async (
  params: AddressBookEntryByAddressParams
): Promise<AddressBookListEntry | null> => {
  return await denApi.get<
    AddressBookListEntry | null,
    AddressBookEntryByAddressParams
  >('organization/address-book/by-address', params);
};

/**
 * Fetch a single exact address-book entry by address.
 *
 * Unlike the paginated list hook, this is for callers that already know the
 * exact checksum address they need to inspect.
 */
export function useAddressBookEntryByAddress(
  address: string | null,
  swrOptions?: SWRConfiguration<AddressBookListEntry | null>
) {
  const swrKey = address
    ? `organization/address-book/by-address/${address}`
    : null;

  const { data, error, isLoading, mutate } =
    useSWR<AddressBookListEntry | null>(
      swrKey,
      address ? () => fetcher({ address }) : null,
      {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        shouldRetryOnError: false,
        ...(swrOptions ?? {}),
      }
    );

  return {
    entry: data ?? null,
    isLoading,
    error,
    refetch: mutate,
  };
}
